import { EditIcon, LoadSpinner } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import React, { useMemo, useState } from 'react'

import { BottomSheet, Card, LineItem } from '@/components'
import { UpdateInstructionForm } from '@/forms/UpdateInstructionForm'
import { useReservationHome, useToggle } from '@/hooks'
import { AccessLevel, useAccessLevel } from '@/hooks/useAccessLevel'
import { AccessInstruction, AccessType, UserType } from '@/types/Homes'
import { AccessInstructions } from '@/views/AccessInstructions/AccessInstructions'

export const HomeAccess = ({ homeId }: { homeId: number }) => {
  const { isLoading, data: homeData, refetch } = useReservationHome({ homeId })

  const accessInstructions = useMemo(() => {
    return homeData && homeData.home?.accessInstructions ? homeData?.home.accessInstructions : []
  }, [homeData])

  const { AGM, HUB_MANAGER } = AccessLevel
  const { accessGranted } = useAccessLevel()
  const isEditable = accessGranted([AGM, HUB_MANAGER])
  const [editInstruction, setEditInstruction] = useState<AccessInstruction>()
  const { isShown: showModal, toggle: toggleModal } = useToggle()

  const handleEditInstruction = (instruction: AccessInstruction) => {
    setEditInstruction(instruction)
    !showModal && toggleModal()
  }

  const handleCloseModal = () => {
    setEditInstruction(undefined)
    toggleModal()
  }

  return (
    <>
      {isLoading ? (
        <div className="flex h-120 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          <AccessContainerStyled>
            {homeData && (
              <>
                <Card cardClassName="mb-4" headerText="General" minimizable={true}>
                  <div className="flex w-full flex-col px-4">
                    <LineItem label="Lock Type" value={homeData.home.lockType} />
                    <LineItem label="Access Setup Instructions" value={homeData.home.accessInstructionsSetup} />
                    <LineItem
                      label="Trash Instructions"
                      value={homeData.home?.property?.trashInstructions || 'Unknown'}
                    />
                    <LineItem
                      label="Mail Box Instructions"
                      value={homeData.home?.property?.mailboxInstructions || 'Unknown'}
                    />
                  </div>
                </Card>
                <AccessInstructions
                  data={accessInstructions}
                  handleEditInstruction={handleEditInstruction}
                  isEditable={isEditable}
                  isLoading={isLoading}
                />
              </>
            )}
          </AccessContainerStyled>
        </>
      )}

      <BottomSheet header="Update Instruction" isVisible={showModal} onClose={handleCloseModal}>
        {editInstruction && (
          <UpdateInstructionForm
            accessInstruction={editInstruction}
            closeModal={handleCloseModal}
            refetchOnSuccess={refetch}
          />
        )}
      </BottomSheet>
    </>
  )
}

interface InstructionProps {
  editInstruction?: (instruction: AccessInstruction) => void
  instruction?: AccessInstruction
  isEditable?: boolean
}

export function Instruction({ instruction, isEditable = false, editInstruction }: InstructionProps) {
  if (
    !instruction ||
    instruction.accessType === AccessType.PARKING ||
    instruction.accessType === AccessType.CHECKOUT ||
    instruction.userType === UserType.HOST ||
    instruction.userType === UserType.ADMIN
  ) {
    return null
  }
  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between">
        <h6 className="py-2">{instruction.name}</h6>
        {isEditable && (
          <EditIcon
            className="w-4 cursor-pointer text-secondary"
            onClick={() => editInstruction && editInstruction(instruction)}
          />
        )}
      </div>
      <p className="mb-4 text-p-sm text-secondary">{instruction.instruction}</p>
    </div>
  )
}

export const AccessContainerStyled = styled.div`
  padding: 1rem 0;
  gap: 1.5rem;
`
